/**
 * Shared BF semantics for the sales dashboard (VIB-1095 F0.4 + VIB-1094 F0.3).
 *
 * ONE pure module with every BF definition the dashboard panels consume, built
 * on the canonical BF engine — never a competing inline variant (jf. funn
 * TOPP-4 / DUP-H1). Inputs are plain already-fetched rows; hooks do the fetch
 * and pass `now` in, so every function here is deterministic and unit-tested.
 *
 *   - `projects.bf_actual`    — canonical realised BF (recompute_project_bf).
 *   - `projects.bf_estimated` — canonical forecast BF (recompute_project_bf_estimated).
 *   - `projects.total_amount` — contract sum.
 * We READ these; we never recompute them here.
 *
 * Month/period attribution is Oslo-time via dashboard-period.ts. Internal
 * projects (is_internal) are excluded from every sales/pipeline aggregate.
 *
 * No Supabase/React imports.
 */

// VIB-1452 terminologi: interne `bf*`-navn beholdes; alle labels og tekster
// som brukeren ser omtaler den samme metrikken som GEEK.
import {
  type DashboardPeriod,
  periodContains,
  periodBounds,
  remainingWeeks,
} from "./dashboard-period";
import { pickClosedDate, isSalesProject } from "./offer-closed-date";
import { geekForMaal } from "./geek-maal-adjustment";
import { osloDateParts } from "./oslo-time";

// ── Input shapes ───────────────────────────────────────────────────────

export interface DashboardProject {
  id: string;
  project_code?: string | null;
  is_internal: boolean | null;
  /** active | kommende | completed | cancelled */
  project_status: string;
  total_amount: number | null;
  bf_estimated: number | null;
  bf_actual: number | null;
  completed_at: string | null;
  created_at: string;
  estimated_deadline: string | null;
  /** VIB-1297: shape-only for now — not consumed by any BF/pipeline calc yet. */
  estimated_start_date?: string | null;
  /** Linked offer's accepted_at — closed-date source (F0.1). */
  offer_accepted_at?: string | null;
  /** Customer, for Hall of Fame's "best customer" aggregate (F3). */
  customer_id?: string | null;
}

export interface DashboardOwner {
  project_id: string;
  user_id: string;
  sales_unit_id: string | null;
  ownership_pct: number | null;
}

/** Pipeline = a live sale not yet delivered. */
const PIPELINE_STATUSES = new Set(["active", "kommende"]);

// ── Attribution helper ─────────────────────────────────────────────────

/**
 * Sum a per-project value across an eligible project set.
 * - Team scope (userId null/undefined): project-level value, summed once per
 *   project (avoids ownership rounding drift).
 * - Person scope: the person's owner share (value × ownership_pct / 100),
 *   summed over their owner rows on eligible projects.
 */
function sumAttributed(
  eligible: DashboardProject[],
  owners: DashboardOwner[],
  userId: string | null | undefined,
  valueOf: (p: DashboardProject) => number,
): number {
  if (!userId) {
    return eligible.reduce((acc, p) => acc + valueOf(p), 0);
  }
  const eligibleById = new Map(eligible.map((p) => [p.id, p]));
  let sum = 0;
  for (const o of owners) {
    if (o.user_id !== userId) continue;
    const p = eligibleById.get(o.project_id);
    if (!p) continue;
    sum += valueOf(p) * (Number(o.ownership_pct ?? 0) / 100);
  }
  return sum;
}

const numOf = (v: number | null) => Number(v ?? 0);

// ── BF levert (delivered) ────────────────────────────────────────────────

/**
 * Realised BF for sales projects completed within the period (by completed_at,
 * Oslo). 2026 uses geekForMaal so pre-2026 work is excluded from targets.
 * Team total or per-person (ownership-weighted). VIB-1096.
 */
export function bfLevert(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null } = {},
): number {
  const eligible = projects.filter(
    (p) =>
      isSalesProject(p) &&
      p.completed_at != null &&
      periodContains(period, p.completed_at),
  );
  return Math.round(
    sumAttributed(eligible, owners, opts.userId, (p) =>
      geekForMaal(p, p.completed_at ? osloDateParts(p.completed_at).year : period.year),
    ),
  );
}

// ── Closede salg (closed sales) ───────────────────────────────────────────

export interface ClosedSalesResult {
  /** Σ total_amount for deals closed in the period. */
  valueKr: number;
  /** Σ bf_estimated for those deals. */
  estBfKr: number;
}

/**
 * Signed-deal value + estimated BF for sales projects whose closed date
 * (offer.accepted_at, else created_at) falls in the period. VIB-1098.
 */
export function closedeSalg(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null } = {},
): ClosedSalesResult {
  const eligible = projects.filter(
    (p) => isSalesProject(p) && periodContains(period, pickClosedDate(p, p.offer_accepted_at)),
  );
  return {
    valueKr: Math.round(sumAttributed(eligible, owners, opts.userId, (p) => numOf(p.total_amount))),
    estBfKr: Math.round(sumAttributed(eligible, owners, opts.userId, (p) => numOf(p.bf_estimated))),
  };
}

/**
 * Closede salg per måned i `year` for hele teamet, med prosjektene bak.
 * Samme regel som `closedeSalg`: salgsprosjekter, lukkedato = akseptert
 * tilbud ellers opprettet, Oslo-måned. Indeks 0 = januar (VIB-2990).
 */
export function closedSalesByMonth(
  projects: DashboardProject[],
  year: number,
): Array<{ estBfKr: number; projects: DashboardProject[] }> {
  const months = Array.from({ length: 12 }, () => ({ estBfKr: 0, projects: [] as DashboardProject[] }));
  for (const p of projects) {
    if (!isSalesProject(p)) continue;
    const { year: y, month } = osloDateParts(pickClosedDate(p, p.offer_accepted_at));
    if (y !== year) continue;
    months[month - 1].estBfKr += numOf(p.bf_estimated);
    months[month - 1].projects.push(p);
  }
  for (const m of months) {
    m.estBfKr = Math.round(m.estBfKr);
    m.projects.sort((a, b) => numOf(b.bf_estimated) - numOf(a.bf_estimated));
  }
  return months;
}

// ── Pipeline BF ────────────────────────────────────────────────────────

/**
 * Expected BF still in the pipeline: Σ bf_estimated for sales projects that
 * are accepted but not yet completed (status active/kommende). Live snapshot,
 * not period-bound. Team or per-person. VIB-1103.
 */
export function pipelineBf(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  opts: { userId?: string | null } = {},
): number {
  const eligible = projects.filter(
    (p) => isSalesProject(p) && PIPELINE_STATUSES.has(p.project_status),
  );
  return Math.round(sumAttributed(eligible, owners, opts.userId, (p) => numOf(p.bf_estimated)));
}

// ── BF-gap ───────────────────────────────────────────────────────────────

export interface BfGapResult {
  targetKr: number;
  levertKr: number;
  gapKr: number;
  /** Needed BF per remaining week to hit target. 0 once target is met. */
  behovPerUkeKr: number;
}

/**
 * Gap between target and delivered, plus required BF per remaining week of the
 * period. VIB-1099. `now` drives the remaining-weeks count (Oslo).
 */
export function bfGap(
  targetKr: number,
  levertKr: number,
  period: DashboardPeriod,
  now: string | number | Date,
): BfGapResult {
  const gapKr = Math.round(targetKr - levertKr);
  const weeks = remainingWeeks(period, now);
  const behovPerUkeKr = gapKr <= 0 || weeks === 0 ? 0 : Math.round(gapKr / weeks);
  return { targetKr: Math.round(targetKr), levertKr: Math.round(levertKr), gapKr, behovPerUkeKr };
}

// ── Feilbudsjettering (budget precision) ──────────────────────────────────

export interface FeilbudsjetteringResult {
  bfEstimatedKr: number;
  bfActualKr: number;
  /** bfActual − bfEstimated (kr). Positive = beat the estimate. */
  deviationKr: number;
  /** (bfActual − bfEstimated) / bfEstimated × 100. null if no estimate base. */
  deviationPct: number | null;
  projectCount: number;
}

/**
 * Weighted budget-precision deviation for sales projects completed in the
 * period. Aggregated (Σ-based) so larger projects weigh more — rewards precise
 * budgets. Team or per-person. VIB-1101 / definition in F0.4.
 */
export function feilbudsjettering(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null } = {},
): FeilbudsjetteringResult {
  const eligible = projects.filter(
    (p) =>
      isSalesProject(p) &&
      p.project_status === "completed" &&
      p.completed_at != null &&
      periodContains(period, p.completed_at) &&
      numOf(p.bf_estimated) > 0,
  );
  const bfEstimatedKr = Math.round(
    sumAttributed(eligible, owners, opts.userId, (p) => numOf(p.bf_estimated)),
  );
  const bfActualKr = Math.round(
    sumAttributed(eligible, owners, opts.userId, (p) => numOf(p.bf_actual)),
  );
  const deviationKr = bfActualKr - bfEstimatedKr;
  const deviationPct =
    bfEstimatedKr > 0 ? Math.round((deviationKr / bfEstimatedKr) * 1000) / 10 : null;
  // Per-person project count = projects the person owns within the set.
  const projectCount = opts.userId
    ? new Set(
        owners
          .filter((o) => o.user_id === opts.userId && eligible.some((p) => p.id === o.project_id))
          .map((o) => o.project_id),
      ).size
    : eligible.length;
  return { bfEstimatedKr, bfActualKr, deviationKr, deviationPct, projectCount };
}

// ── BF Forecast ────────────────────────────────────────────────────────

/**
 * Expected BF at period end: delivered BF (completed in period) + Σ
 * bf_estimated for active/kommende sales projects whose estimated_deadline
 * lands in the period. VIB-1097. Replaces useBfTrend's linear projection.
 */
export function bfForecast(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null } = {},
): number {
  const delivered = bfLevert(projects, owners, period, opts);
  const pending = projects.filter(
    (p) =>
      isSalesProject(p) &&
      PIPELINE_STATUSES.has(p.project_status) &&
      p.estimated_deadline != null &&
      periodContains(period, p.estimated_deadline),
  );
  const expected = Math.round(
    sumAttributed(pending, owners, opts.userId, (p) => numOf(p.bf_estimated)),
  );
  return delivered + expected;
}

// ── deriveBfTarget (F0.3) ──────────────────────────────────────────────

export interface BfTargetSalesUnit {
  id: string;
  /** Display name; optional because calculation-only callers do not need it. */
  name?: string;
  bf_target_mnok: number | null;
  year: number;
  memberUserIds: string[];
}

/** Fraction of the annual target that the period represents. */
function periodFractionOfYear(period: DashboardPeriod): number {
  switch (period.granularity) {
    case "month":
      return 1 / 12;
    case "quarter":
      return 1 / 4;
    case "year":
      return 1;
    case "custom": {
      const { fromDate, toDate } = periodBounds(period);
      const customDays =
        Math.round(
          (Date.parse(`${toDate}T00:00:00Z`) - Date.parse(`${fromDate}T00:00:00Z`)) / 86_400_000,
        ) + 1;
      const yearStart = Date.parse(`${period.year}-01-01T00:00:00Z`);
      const yearEnd = Date.parse(`${period.year}-12-31T00:00:00Z`);
      const yearDays = Math.round((yearEnd - yearStart) / 86_400_000) + 1;
      return customDays / yearDays;
    }
  }
}

/**
 * Derive a BF target (kr) for a person or the whole team in a period, from the
 * annual per-unit targets in `sales_units.bf_target_mnok` (no separate target
 * table). VIB-1094.
 *
 * Målsemantikk låst av Erik 16.07 (VIB-1285): enhetsmålet er FELLES og
 * udelelig — det deles ALDRI på medlemsantall. En persons «mål» er enhetens
 * fulle mål; personens tall er et BIDRAG mot det. Måloppnåelse evalueres kun
 * på enhetsnivå (se `unitGoalStatus`).
 *
 * - Quarter = annual / 4, month = annual / 12, custom = pro-rata on days.
 * - Team (no userId): Σ of every same-year unit's period target.
 * - Person: Σ of the FULL period target of every unit they belong to.
 */
export function deriveBfTarget(input: {
  salesUnits: BfTargetSalesUnit[];
  period: DashboardPeriod;
  userId?: string | null;
}): number {
  const { salesUnits, period, userId } = input;
  const fraction = periodFractionOfYear(period);
  let total = 0;
  for (const su of salesUnits) {
    if (su.year !== period.year) continue;
    const unitAnnualKr = numOf(su.bf_target_mnok) * 1_000_000;
    const unitPeriodKr = unitAnnualKr * fraction;
    if (!userId || su.memberUserIds.includes(userId)) {
      total += unitPeriodKr;
    }
  }
  return Math.round(total);
}

// ── unitGoalStatus (VIB-1285) ──────────────────────────────────────────

export interface UnitGoalStatus {
  /** Σ FULLE periodemål for enhetene brukeren er medlem av. */
  targetKr: number;
  /** Enhetens samlede oppnåelse: Σ attribuert BF over ALLE medlemmene. */
  unitLevertKr: number;
  /** Delt status: sum(medlemmenes attribuerte BF) >= enhetsmål. Gjelder alle medlemmer likt. */
  achieved: boolean;
}

/**
 * Enhetens måloppnåelse sett fra ett medlem (målsemantikk låst av Erik 16.07,
 * VIB-1285): målet er felles og udelelig, og oppnådd/ikke-oppnådd evalueres
 * KUN på enhetsnivå — `sum(medlemmenes attribuerte BF) >= enhetsmål`. Det
 * finnes bevisst ingen per-person-status her; personens eget tall er et bidrag
 * (bruk `bfLevert(..., { userId })` for det).
 *
 * Oppnåelsen summeres over medlemmenes person-attribusjon (ownership_pct),
 * ikke over radenes `sales_unit_id` — robust mot legacy-rader uten enhet.
 */
export function unitGoalStatus(input: {
  projects: DashboardProject[];
  owners: DashboardOwner[];
  salesUnits: BfTargetSalesUnit[];
  period: DashboardPeriod;
  userId: string;
}): UnitGoalStatus {
  const { projects, owners, salesUnits, period, userId } = input;
  const myUnits = salesUnits.filter(
    (su) => su.year === period.year && su.memberUserIds.includes(userId),
  );
  const targetKr = deriveBfTarget({ salesUnits: myUnits, period, userId });
  const memberIds = [...new Set(myUnits.flatMap((su) => su.memberUserIds))];
  const unitLevertKr = memberIds.reduce(
    (sum, memberId) => sum + bfLevert(projects, owners, period, { userId: memberId }),
    0,
  );
  return { targetKr, unitLevertKr, achieved: targetKr > 0 && unitLevertKr >= targetKr };
}
